import { useCallback, useMemo, useState } from 'react';

import { supabaseClient, useUser } from '@auth-v2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { addUserClubs, Coach, languages } from '@data';
import { useTranslate } from '@i18n';
import {
    useClubCollections,
    useCoaches,
    useUpdateUserCoaches,
    useUpdateUserSettings,
    useUserClubs,
    useUserCoaches,
    useUserSettingsSafe,
} from '@queries';

type UseUserWelcomeWizardParams = {
    totalSteps: number;
    onDismiss: () => void;
};

const createUserWelcomeWizardFormSchema = (dobRequiredMessage: string) =>
    z.object({
        locale: z.enum(languages),
        userPreferredUnit: z.enum(['metric', 'imperial', 'mixed']),
        dateOfBirth: z
            .date()
            .nullable()
            .refine((value): boolean => value !== null, {
                message: dobRequiredMessage,
            }),
        sex: z.enum(['M', 'F', 'O']).nullable().optional(),
        handicap: z.number().int().min(-10).max(54).default(54),
        clubIds: z.array(z.number()),
        handedness: z.enum(['right', 'left']),
        coachIds: z.array(z.string()),
    });

export type UserWelcomeWizardForm = z.infer<ReturnType<typeof createUserWelcomeWizardFormSchema>>;

const parseDob = (value: string | null | undefined) => {
    if (!value) {
        return null;
    }

    const date = parseISO(value);

    return isValid(date) ? date : null;
};

export const useUserWelcomeWizard = ({ totalSteps, onDismiss }: UseUserWelcomeWizardParams) => {
    const t = useTranslate();
    const schema = useMemo(() => createUserWelcomeWizardFormSchema(t('user_welcome_wizard.age_required_error')), [t]);

    const [activeStepIndex, setActiveStepIndex] = useState(0);

    const { data: user } = useUser();

    const { data: userSettings } = useUserSettingsSafe(user?.id);

    const { data: clubs, isLoading: isLoadingClubs } = useClubCollections();

    const { data: userClubs = [], isLoading: isLoadingUserClubs } = useUserClubs(user?.id);

    const { data: coaches = [], isLoading: isLoadingCoaches } = useCoaches();

    const { data: userCoaches = [], isLoading: isLoadingUserCoaches } = useUserCoaches();

    const { mutateAsync: updateUserSettings } = useUpdateUserSettings(user?.id);

    const { mutateAsync: updateUserCoaches } = useUpdateUserCoaches(user?.id || '');

    const defaultValues = useMemo<UserWelcomeWizardForm | undefined>(() => {
        if (!userSettings) {
            return undefined;
        }

        return {
            locale: userSettings.locale,
            userPreferredUnit: userSettings.userPreferredUnit,
            dateOfBirth: parseDob(userSettings.dateOfBirth),
            sex: userSettings.sex ?? null,
            handicap: userSettings.handicap ?? 54,
            handedness: userSettings.leftHanded ? 'left' : 'right',
            clubIds: userClubs.map((club) => club.id),
            coachIds: userCoaches.map((coach) => coach.coachId),
        };
    }, [userClubs, userCoaches, userSettings]);

    const form = useForm<UserWelcomeWizardForm>({
        resolver: zodResolver(schema),

        defaultValues: {
            locale: 'en',
            userPreferredUnit: 'metric',
            dateOfBirth: null,
            handicap: 54,
            sex: null,
            clubIds: [],
            handedness: 'right',
            coachIds: [],
        },

        values: defaultValues,
    });

    const coachOptions = useMemo(
        () =>
            coaches.map((coach: Coach) => ({
                value: coach.userId,
                label: coach.name,
            })),
        [coaches],
    );

    const saveMutation = useMutation({
        mutationFn: async (values: UserWelcomeWizardForm) => {
            if (!user?.id) {
                throw new Error('Missing user');
            }

            await updateUserSettings({
                locale: values.locale,
                userPreferredUnit: values.userPreferredUnit,
                dateOfBirth: values.dateOfBirth ? format(values.dateOfBirth, 'yyyy-MM-dd') : null,
                sex: values.sex ?? null,
                handicap: values.handicap,
                leftHanded: values.handedness === 'left',
            });

            await addUserClubs(supabaseClient, user.id, values.clubIds);

            await updateUserCoaches(values.coachIds);
        },

        onSuccess: () => {
            onDismiss();
        },

        onError: (error) => {
            console.error(error);

            toast.error(t('user_welcome_wizard.save_error'));
        },
    });

    const persistAndDismiss = form.handleSubmit((values) => {
        saveMutation.mutate(values);
    });

    const handleNext = useCallback(() => {
        if (activeStepIndex >= totalSteps - 1) {
            onDismiss();
            return;
        }

        setActiveStepIndex((s) => s + 1);
    }, [activeStepIndex, onDismiss, totalSteps]);

    const handleBack = useCallback(() => {
        setActiveStepIndex((s) => Math.max(0, s - 1));
    }, []);

    return {
        form,
        activeStepIndex,
        clubs,
        coachOptions,

        isLoadingClubs,
        isLoadingUserClubs,
        isLoadingCoaches,
        isLoadingUserCoaches,

        isSaving: saveMutation.isPending,

        persistAndDismiss,
        handleNext,
        handleBack,
    };
};
